import React, { memo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import { Translation, useTranslation } from 'react-i18next'
import * as _ from 'lodash'
import { FormControl, Hidden, InputLabel } from '@material-ui/core'
import Datetime from 'react-datetime'
import Fab from '@material-ui/core/Fab'
import moment from 'moment'

import styles from '../styles'
import { EJobActionTypes, EJobTextCategory } from 'models'
import classNames from 'classnames'
import { JobEditor, Media } from './'
import { StoreValue } from 'store'
import Input from '@material-ui/core/Input'

const useStyles = makeStyles(styles)

const Step1View: React.FunctionComponent<any> = props => {
	const { storeDispatch, handleNext, job } = props
	const { title, jobDescription, expiryDate, applyJobExternalUrl, error } =
		job
	const classes = useStyles()
	const { t } = useTranslation()

	const scrollToErrorField = useCallback(
		(errorFields: {
			title: boolean
			jobDescription: boolean
			expiryDate: boolean
			// applyJobExternalUrl: boolean
		}) => {
			// I need to scroll to the highest error field.
			let elementToScrollTo: HTMLElement | null
			const { title, jobDescription, expiryDate } = errorFields

			if (title) {
				elementToScrollTo = document.querySelector('#title')
			} else if (jobDescription) {
				elementToScrollTo = document.querySelector('#jobDescription')
			} else if (expiryDate) {
				elementToScrollTo = document.querySelector('#expiryDate')
			} else if (applyJobExternalUrl) {
				elementToScrollTo = document.querySelector(
					'#applyJobExternalUrl',
				)
			} else {
				elementToScrollTo = null
			}

			elementToScrollTo?.scrollIntoView({
				behavior: 'smooth',
			})
		},
		[applyJobExternalUrl],
	)

	const handleApplyJobExternalUrl = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			storeDispatch({
				type: EJobActionTypes.setApplyJobExternalUrl,
				applyJobExternalUrl: event.target.value,
			})
		},
		[storeDispatch],
	)

	const updateExpirationDatetime = useCallback(
		dt => {
			storeDispatch({
				type: EJobActionTypes.setExpirationDate,
				expiryDate: dt,
			})
		},
		[storeDispatch],
	)

	const handleTitleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// setTitle(event.target.value)
			storeDispatch({
				type: EJobActionTypes.setTitle,
				title: event.target.value,
			})
		},
		[storeDispatch],
	)

	const isNotValidStep = useCallback(() => {
		return (
			_.isEmpty(title) ||
			_.isEmpty(jobDescription) ||
			_.isEmpty(expiryDate)
		)
	}, [title, jobDescription, expiryDate])

	const goToNextStep = useCallback(
		_error => {
			if (isNotValidStep()) {
				storeDispatch({
					type: EJobActionTypes.setError,
					errors: {
						title: _.isEmpty(title),
						jobDescription: _.isEmpty(jobDescription),
						expiryDate: _.isEmpty(expiryDate),
					},
				})
				scrollToErrorField({
					title: _.isEmpty(title),
					jobDescription: _.isEmpty(jobDescription),
					expiryDate: _.isEmpty(expiryDate),
				})
				return
			}
			handleNext()
		},
		[
			scrollToErrorField,
			storeDispatch,
			isNotValidStep,
			handleNext,
			expiryDate,
			jobDescription,
			title,
		],
	)

	return (
		<div>
			<Media />
			<div
				id="title"
				style={{ width: '100%' }}
				className={classes.fieldContainer}
			>
				<FormControl className={classes.formControl}>
					<InputLabel htmlFor="jobName">
						<Translation key="jobNameRequired">
							{t => (
								<div
									className={classNames(classes.labelField, {
										[classes.labelFieldError]:
											error && error.title,
									})}
								>
									{t('Name of your Job')}
									<span
										className={classNames(
											classes.requiredField,
											{
												[classes.labelFieldError]:
													error && error.title,
											},
										)}
									>
										{`(${t('Required')})`}
									</span>
								</div>
							)}
						</Translation>
					</InputLabel>
					<Input
						id="jobName"
						className={classNames(classes.textField, {
							[classes.labelFieldError]: error && error.title,
						})}
						value={title}
						onChange={handleTitleChange}
					/>
				</FormControl>
			</div>
			{/* <Divider className={classes.divider} /> */}
			<div
				id="jobDescription"
				className={classNames(classes.editorRoot, classes.mt30)}
			>
				<div
					className={classNames(classes.labelTextField, {
						[classes.labelFieldError]: error.jobDescription,
					})}
				>
					{t('Describe your job')}
					<span className={classes.requiredField}>
						{`(${t('Required')})`}
					</span>
				</div>
				<JobEditor textCategory={EJobTextCategory.description} />
			</div>
			{/*<Divider className={classes.divider} />*/}

			<div id="expiryDate" className={classes.btnsContainer}>
				<Datetime
					className={classes.inputDateTime}
					timeFormat={false}
					value={moment(expiryDate)}
					closeOnSelect={true}
					onChange={updateExpirationDatetime}
					inputProps={{
						placeholder: t('Expiration date'),
						readOnly: true,
					}}
					input={true}
					renderInput={(props: any, open: any, close: any) => {
						return (
							<FormControl
								style={{
									width: '100%',
								}}
								className={classes.formControl}
							>
								<InputLabel htmlFor="experience_date">
									<Translation key="experienceDate">
										{t => (
											<div
												className={classNames(
													classes.labelField,
													{
														[classes.labelFieldError]:
															error.expiryDate,
													},
												)}
											>
												<Hidden>
													{t(
														'Expiration date of the opening',
													)}{' '}
												</Hidden>
												<span
													className={classNames(
														classes.requiredField,
														{
															[classes.labelFieldError]:
																error.expiryDate,
														},
													)}
												>
													{`(${t('Required')})`}
												</span>
											</div>
										)}
									</Translation>
								</InputLabel>
								<Input {...props} onClick={open} />
							</FormControl>
						)
					}}
				/>
			</div>
			<div
				id="applyJobExternalUrl"
				style={{ width: '100%' }}
				className={classes.fieldContainer}
			>
				<FormControl className={classes.formControl}>
					<InputLabel htmlFor="applyJobExternalUrl">
						<Translation key="applyJobExternalUrlNonRequired">
							{t => (
								<div
									className={classNames(classes.labelField, {
										[classes.labelFieldError]:
											error && error.applyJobExternalUrl,
									})}
								>
									{t('External apply URL')}
								</div>
							)}
						</Translation>
					</InputLabel>
					<Input
						id="applyJobExternalUrl"
						className={classNames(classes.inputDateTime, {
							[classes.labelFieldError]:
								error && error.applyJobExternalUrl,
						})}
						value={applyJobExternalUrl}
						onChange={handleApplyJobExternalUrl}
					/>
				</FormControl>
			</div>
			<div className={classes.nextBtnCont}>
				<Fab
					onClick={() => goToNextStep(error)}
					className={classes.nextBtn}
					variant="extended"
					size="medium"
					color="primary"
					aria-label="Add"
				>
					{t('Next') || ''}
				</Fab>
			</div>
		</div>
	)
}

const Step1 = (props: any) => (
	<StoreValue keys={['user.id|storeUserId', 'job', 'storeDispatch']}>
		<Step1View {...props} />
	</StoreValue>
)

export default memo(Step1)
